import {FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {TenantList} from "../components/TenantList";
import {TenantResource} from "../enums/TenantResource";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {
    AresDeploymentEnvironment,
    AresDeploymentExecutionPhase,
    AresDeploymentAres, AresDeploymentGroup, AresDeploymentWorkflowDefinition,
    AresDeploymentGroupInstallationStep,
    AresDeploymentInstallationStepPoint, Company, CompanyDto, CompanySettings,
    DeploymentGroupStepTriggerType, InstallationVariableType,
    InvoiceSumType, PagedResult
} from "../API";
import {
    CheckboxElement,
    CustomRenderElement, CMEditorElement,
    CustomSearchBoxElement, EnumDropdownElement,
    ITenantFormLayout, NumberElement, MultilineTextElement,
    TextFieldElement, TimeElement,
    TitleElement
} from "../components/OnixElements";
import {AuthContext} from "../contexts/AuthContext";
import {useTranslation} from "react-i18next";
import {TitleAndContentLayout} from "../components/TitleAndContentLayout";
import Axios from "axios";
import {
    ActionButton,
    Checkbox,
    Pivot,
    PivotItem,
    ProgressIndicator,
    SelectionMode,
    Spinner,
    Stack,
    Text
} from "@fluentui/react";
import {ListItemCheckbox} from "../components/AsCheckMark";
import {GetApiError} from "../components/ShowApiError";
import {OpenCompany} from "../components/LinkTo";
import moment from "moment";
import {MakeHub} from "../MakeHub";
import {useNavigate} from "react-router-dom";
import {AppRoute} from "../AppRoutes";
import {AresDeploymentRolloutSection} from "../components/AresDeploymentRolloutSection";
import {AresDeploymentPrepareSnapshotCallout} from "../components/AresDeploymentPrepareSnapshotCallout";
import {AresDeploymentBatchPrepareSnapshotCallout} from "../components/AresDeploymentBatchPrepareSnapshotCallout";

type AresDeploymentGroupSnapshotSummary = {
    id: number;
    aresDeploymentGroupId: number;
    versionNo: number;
    name: string;
    notes?: string;
    sourceHash?: string;
    snapshotTakenAt: string;
    isTesting: boolean;
    isStaging: boolean;
    isProduction: boolean;
    createdOn?: string;
    createdBy?: string;
};

export const AresDeploymentsPage: FC = () => {
    const navigate = useNavigate();
    const {UserInfo} = useContext(AuthContext);
    const {t} = useTranslation();
    const [aresGroupSelection, setAresGroupSelection] = useState<Partial<AresDeploymentGroup> | null>();
    const [selectedAresGroups, setSelectedAresGroups] = useState<AresDeploymentGroup[]>([]);
    const [mainDeploymentCompany, setMainDeploymentCompany] = useState<CompanySettings | null>(null);
    const [mainTestDeploymentCompany, setMainTestDeploymentCompany] = useState<CompanySettings | null>(null);
    const reloadGroupsRef = useRef<() => Promise<PagedResult<AresDeploymentGroup>>>(null);
    const reloadAresesRef = useRef<() => Promise<PagedResult<AresDeploymentAres>>>(null);
    const [groupSnapshots, setGroupSnapshots] = useState<AresDeploymentGroupSnapshotSummary[]>([]);
    const [groupSnapshotsLoading, setGroupSnapshotsLoading] = useState<boolean>(false);
    const [updatingCompanies, setUpdatingCompaniesOnMessage] = useState<{ [key: string]: boolean }>({});
    const [bulkUpdating, setBulkUpdating] = useState<boolean>(false);
    const [bulkUpdateError, setBulkUpdateError] = useState<string | null>(null);
    useEffect(() => {
        MakeHub<{ [key: string]: boolean }>(
            `AresSyncHub`,
            "AresSyncChanged",
            (onMessage) => {
                console.log("AresSyncChanged", onMessage);
                setUpdatingCompaniesOnMessage(onMessage);
            },
            (onConnectionChanged) => {
            },
            false,
        );
    }, []);

    useEffect(() => {
        Axios.get("CompanySettings/MainAresCompany")
            .then(response => {
                setMainDeploymentCompany(response.data);
            })
            .catch(error => {
                alert(`Error fetching main deployment company: ${error}`);
            });

        Axios.get("CompanySettings/MainAresTestingCompany")
            .then(response => {
                setMainTestDeploymentCompany(response.data);
            })
            .catch(() => {
                setMainTestDeploymentCompany(null);
            });
    }, []);

    const loadSnapshots = useCallback(async (groupId?: number) => {
        if (!groupId) {
            setGroupSnapshots([]);
            return;
        }

        setGroupSnapshotsLoading(true);
        try {
            const {data} = await Axios.get<AresDeploymentGroupSnapshotSummary[]>(
                `/AresDeploymentGroupSnapshot/ListForGroup?groupId=${groupId}`
            );
            setGroupSnapshots(data ?? []);
        } catch (e) {
            alert(`Error fetching snapshots: ${GetApiError(e)}`);
            setGroupSnapshots([]);
        } finally {
            setGroupSnapshotsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSnapshots(aresGroupSelection?.id);
    }, [aresGroupSelection?.id, loadSnapshots]);

    const refreshSnapshotsAfterCreate = useCallback(async (groupId?: number) => {
        if (!groupId) {
            return;
        }

        try {
            const {data} = await Axios.get<AresDeploymentGroupSnapshotSummary[]>(
                `/AresDeploymentGroupSnapshot/ListForGroup?groupId=${groupId}`
            );
            setGroupSnapshots(data ?? []);
        } catch {
        }
    }, []);

    const refreshAfterRolloutRelease = useCallback(async () => {
        await loadSnapshots(aresGroupSelection?.id);
        await reloadGroupsRef.current?.();
    }, [aresGroupSelection?.id, loadSnapshots]);

    const setSnapshotEnvironment = useCallback(async (
        snapshotId: number,
        environment: AresDeploymentEnvironment,
        enabled: boolean,
        groupId?: number
    ) => {
        try {
            await Axios.post(
                `/AresDeploymentGroupSnapshot/SetEnvironment?snapshotId=${snapshotId}&environment=${environment}&enabled=${enabled}`
            );
            await loadSnapshots(groupId);
            await reloadGroupsRef.current?.();
        } catch (e) {
            alert(`Error assigning snapshot environment: ${GetApiError(e)}`);
        }
    }, [loadSnapshots]);

    const compactGroupSnapshots = useMemo(() => {
        const sortedByVersionDesc = [...groupSnapshots].sort((a, b) => b.versionNo - a.versionNo);
        const latestThree = sortedByVersionDesc.slice(0, 3);
        const assignedToEnvironment = sortedByVersionDesc.filter(v => v.isTesting || v.isStaging || v.isProduction);

        const mergedById = new Map<number, AresDeploymentGroupSnapshotSummary>();
        for (const snapshot of latestThree) {
            mergedById.set(snapshot.id, snapshot);
        }

        for (const snapshot of assignedToEnvironment) {
            mergedById.set(snapshot.id, snapshot);
        }

        return [...mergedById.values()].sort((a, b) => b.versionNo - a.versionNo);
    }, [groupSnapshots]);

    const nextSnapshotVersionName = useMemo(() => {
        const maxVersion = groupSnapshots.reduce((prev, curr) => Math.max(prev, curr.versionNo ?? 0), 0);
        return `v${maxVersion + 1}`;
    }, [groupSnapshots]);

    const groupForm = useMemo<ITenantFormLayout<AresDeploymentGroup>>(() => ({
        name: {
            type: new TextFieldElement({})
        },
        isPublic: {
            type: new CheckboxElement({}),
            translationKey: "public"
        },
        isBackgroundInstallable: {
            type: new CheckboxElement({})
        },
        minPantheonBuildNo: {
            type: new NumberElement({})
        },
        maxPantheonBuildNo: {
            type: new NumberElement({})
        },
        pantheonBuildConstraintsInfo: {
            translationKey: "pantheonBuildConstraintsInfoTitle",
            type: new CustomRenderElement({
                renderer: () => (
                    <Text variant="small">{t("pantheonBuildConstraintsInfo")}</Text>
                )
            })
        },
        activityCheckSqlInfo: {
            translationKey: "activityCheckSqlHelpTitle",
            type: new CustomRenderElement({
                renderer: () => (
                    <Text variant="small">{t("activityCheckSqlHelp")}</Text>
                )
            })
        },
        activityCheckSql: {
            translationKey: "activityCheckSql",
            type: new CMEditorElement({
                codeLanguage: "sql",
                minHeight: 240,
                description: t("activityCheckSqlHelp")
            })
        },
        isInvoicedToAccounting: {
            type: new CheckboxElement({})
        },
    }), [t]);

    const groupColumns = useMemo(() => MakeTenantColumns<AresDeploymentGroup>({
        name: {
            minWidth: 200
        },
        minPantheonBuildNo: {
            minWidth: 120,
            render: item => <span>{item.minPantheonBuildNo ?? ""}</span>
        },
        maxPantheonBuildNo: {
            minWidth: 120,
            render: item => <span>{item.maxPantheonBuildNo ?? ""}</span>
        },
        isBackgroundInstallable: {
            minWidth: 160,
            render: item => <ListItemCheckbox checked={item.isBackgroundInstallable}/>
        },
        lastAresTestRunPassed: {
            minWidth: 170,
            render: item => item.lastAresTestRunPassed === undefined || item.lastAresTestRunPassed === null
                ? <span/>
                : <ListItemCheckbox checked={item.lastAresTestRunPassed}/>
        },
        lastSuccessfulAresTestRunAt: {
            minWidth: 180,
            render: item => item.lastSuccessfulAresTestRunAt
                ? <span>{moment(item.lastSuccessfulAresTestRunAt).format("DD.MM.YYYY. HH:mm")}</span>
                : <span/>
        },
        isPublic: {
            minWidth: 90,
            render: item => <ListItemCheckbox checked={item.isPublic}/>,
            translation: "public"
        },
        activityCheckSql: {
            minWidth: 250,
            maxWidth: 400,
            render: item => <span>{item.activityCheckSql ?? ""}</span>
        },
        isInvoicedToAccounting: {
            render: item => <ListItemCheckbox checked={item.isInvoicedToAccounting}/>
        },
    }, t), [t]);

    const aresForm = useMemo<ITenantFormLayout<AresDeploymentAres>>(() => ({
        aresAcKey: {
            translationKey: "acKey",
            type: new CustomSearchBoxElement({
                resource: TenantResource.AresTPaSqli,
                url: "",
                key: "acKey",
                titleKey: "acName",
                subtitleKey: "acKey",
                customSearchBoxType: "DEFAULT_CRUD",
                additionalStringParameter: (dto) => mainDeploymentCompany?.companySQLConnectionString
            })
        },
        aresAcName: {
            translationKey: "acName",
            disabled: true,
            type: new TitleElement({}),
            dependsOn: [{
                key: "aresAcKey",
                onChanged: (data, initialized) => {
                    if (!initialized) return;
                    const storage = (data as any)?.aresAcKeystorage as any;
                    if (!storage) return;
                    return storage.acName;
                }
            }]
        },
        executionPhase: {
            type: new EnumDropdownElement({
                enumData: AresDeploymentExecutionPhase
            })
        },
        executionPhaseOrder: {
            type: new NumberElement({})
        }
    }), [mainDeploymentCompany?.companySQLConnectionString]);

    const renderAresExecutionPhase = useCallback((phase?: AresDeploymentExecutionPhase | null) => {
        const resolvedPhase = phase ?? AresDeploymentExecutionPhase.Standard;
        return t(AresDeploymentExecutionPhase[resolvedPhase]);
    }, [t]);

    const aresColumns = useMemo(() => MakeTenantColumns<AresDeploymentAres>({
        aresAcKey: {
            translation: "acKey"
        },
        aresAcName: {
            translation: "acName",
            minWidth: 500
        },
        aresVersion: {},
        executionPhase: {
            render: item => <span>{renderAresExecutionPhase(item.executionPhase)}</span>,
            minWidth: 220
        },
        executionPhaseOrder: {
            minWidth: 140
        }
    }, t), [renderAresExecutionPhase, t]);

    const aresBaseDto = useMemo<Partial<AresDeploymentAres>>(() => ({
        aresDeploymentGroupId: aresGroupSelection?.id,
        executionPhase: AresDeploymentExecutionPhase.Standard,
    }), [aresGroupSelection?.id]);

    const workflowForm = useMemo<ITenantFormLayout<AresDeploymentWorkflowDefinition>>(() => ({
        acIdentify: {
            translationKey: "acIdentify",
            type: new CustomSearchBoxElement({
                resource: TenantResource.AresTPaWorkflowDefinition,
                key: "acIdentify",
                titleKey: "acName",
                subtitleKey: "acIdentify",
                customSearchBoxType: "DEFAULT_CRUD",
                additionalStringParameter: () => mainDeploymentCompany?.companySQLConnectionString
            })
        },
        acName: {
            translationKey: "acName",
            disabled: true,
            type: new TitleElement({}),
            dependsOn: [{
                key: "acIdentify",
                onChanged: (data, initialized) => {
                    if (!initialized) return;
                    const storage = (data as any)?.acIdentifystorage as any;
                    if (!storage) return;
                    return storage.acName;
                }
            }]
        }
    }), [mainDeploymentCompany?.companySQLConnectionString]);

    const workflowColumns = useMemo(() => MakeTenantColumns<AresDeploymentWorkflowDefinition>({
        acIdentify: {
            translation: "acIdentify"
        },
        acName: {
            translation: "acName",
            minWidth: 300
        }
    }, t), [t]);

    const aresDeploymentGroupInstallationStepForm = useMemo<ITenantFormLayout<AresDeploymentGroupInstallationStep>>(() => ({
        order: {
            type: new NumberElement({})
        },
        variableName: {
            type: new TextFieldElement({})
        },
        variableType: {
            type: new EnumDropdownElement({
                enumData: InstallationVariableType,
            })
        },
        resetsOnInvoicing: {
            type: new CheckboxElement({})
        },
        duration: {
            type: new TimeElement({})
        },
        sumType: {
            type: new EnumDropdownElement({
                enumData: InvoiceSumType
            }),
            translationKey: "invoicingSumType"
        },
        description: {
            type: new MultilineTextElement({
                rows: 3
            }),
        },
        triggerType: {
            type: new EnumDropdownElement({
                enumData: DeploymentGroupStepTriggerType
            })
        }
    }), []);

    const aresDeploymentGroupInstallationStepColumns = useMemo(() => MakeTenantColumns<AresDeploymentGroupInstallationStep>({
        order: {},
        variableName: {},
        variableType: {
            render: item => t(`enum.InstallationVariableType.${InstallationVariableType[item.variableType]}`)
        },
        resetsOnInvoicing: {
            minWidth: 200,
            render: item => <ListItemCheckbox checked={item.resetsOnInvoicing}/>
        },
        duration: {
            render: item => !!item.duration ? (moment.duration(item.duration, "seconds") as any).format("HH:mm", {
                trim: "false"
            }) : ""
        },
        sumType: {
            render: item =>
                <span>{item.sumType !== undefined && item.sumType !== null ? t(`enum.InstallationStepSumType.${InvoiceSumType[item.sumType]}`) : ""}</span>,
            translation: "invoicingSumType",
            minWidth: 200
        },
        triggerType: {
            render: item => item.triggerType !== undefined && item.triggerType !== null ? t(`enum.DeploymentGroupStepTriggerType.${DeploymentGroupStepTriggerType[item.triggerType!]}`) :
                <span></span>
        },
        description: {},
    }, t), [t]);

    const downloadGroupZip = useCallback((groupId: number) => {
        window.open(`/api/AresDeploymentAres/ExportGroupZip?groupId=${groupId}`, "_blank");
    }, []);

    const refreshAresesAfterVersionPull = useCallback(async (groupId?: number) => {
        if (aresGroupSelection?.id === groupId) {
            await reloadAresesRef.current?.();
        }
    }, [aresGroupSelection?.id]);

    const refreshGroupsAfterTests = useCallback(async () => {
        await reloadGroupsRef.current?.();
    }, []);

    const refreshAfterPreparedSnapshot = useCallback(async (groupId?: number) => {
        if (aresGroupSelection?.id === groupId) {
            await refreshSnapshotsAfterCreate(groupId);
        }

        await reloadGroupsRef.current?.();
    }, [aresGroupSelection?.id, refreshSnapshotsAfterCreate]);

    const refreshAfterBatchPreparedSnapshots = useCallback(async () => {
        await reloadGroupsRef.current?.();

        if (aresGroupSelection?.id) {
            await loadSnapshots(aresGroupSelection.id);
            await reloadAresesRef.current?.();
        }
    }, [aresGroupSelection?.id, loadSnapshots]);

    const batchPrepareCommands = useMemo(() => [
        <AresDeploymentBatchPrepareSnapshotCallout
            key="prepare-batch-snapshots"
            keyData="prepare-batch-snapshots"
            groups={selectedAresGroups}
            disabled={selectedAresGroups.length < 2}
            onPrepared={refreshAfterBatchPreparedSnapshots}
        />
    ], [refreshAfterBatchPreparedSnapshots, selectedAresGroups]);

    const groupCommands = useCallback((item: AresDeploymentGroup) => ([
        <ActionButton key="download-zip" iconProps={{iconName: "Download"}}
                      onClick={() => downloadGroupZip(item.id)}>
            Preuzmi grupu (ZIP)
        </ActionButton>,
        <ActionButton
            key="installed-companies"
            iconProps={{iconName: "BulletedList"}}
            onClick={() => navigate(AppRoute.ARES_DEPLOYMENT_GROUP_INSTALLED_COMPANIES(item.id))}
        >
            Instalirana poduzeća
        </ActionButton>,
        <AresDeploymentPrepareSnapshotCallout
            key="prepare-snapshot"
            keyData={`prepare-snapshot-${item.id}`}
            groupId={item.id}
            groupName={item.name}
            suggestedVersionName={
                aresGroupSelection?.id === item.id ? nextSnapshotVersionName : undefined
            }
            onVersionsPulled={() => refreshAresesAfterVersionPull(item.id)}
            onTestsFinished={refreshGroupsAfterTests}
            onSnapshotCreated={() => refreshAfterPreparedSnapshot(item.id)}
        />
    ]), [
        downloadGroupZip,
        navigate,
        aresGroupSelection?.id,
        nextSnapshotVersionName,
        refreshAresesAfterVersionPull,
        refreshGroupsAfterTests,
        refreshAfterPreparedSnapshot,
    ]);

    const [selectedCompany, SetSelectedCompany] = useState<Partial<Company> | undefined | null>(null);
    const [companyErrors, setCompanyErrors] = useState<{ [companyId: number]: string | undefined }>({});

    const companyIsLoading = useCallback((companyId: number) => {
        setCompanyErrors(prev => {
            const copy = {...prev};
            delete copy[companyId];
            return copy;
        });
    }, []);

    const companyIsNotLoading = useCallback((companyId: number, reason?: string) => {
        if (reason) {
            setCompanyErrors(prev => ({...prev, [companyId]: reason}));
        }
    }, []);

    const isCompanyLoading = useCallback((companyId: number) => {
        return updatingCompanies[companyId] === true;
    }, [updatingCompanies]);

    const updateAllAres = useCallback((companyId: number) => {
        const forItemId = companyId;
        companyIsLoading(forItemId);
        Axios.post(`/Company/UpdateAllAreses?companyId=${forItemId}`)
            .then(value => {
                const {data} = value;
                if (data) {
                    const firstKey = Object.keys(data)[0];
                    const res = data[firstKey];
                    if (res !== "OK") {
                        companyIsNotLoading(forItemId, res);
                    }
                }
            })
            .catch(reason => {
                console.log(reason);
                companyIsNotLoading(forItemId, GetApiError(reason));
            })
            .finally(() => companyIsNotLoading(forItemId));
    }, [companyIsLoading, companyIsNotLoading]);

    const updateAllCompaniesWithAres = useCallback(() => {
        const confirmations = [
            "Ova akcija pokreće ažuriranje Ares-a za SVA poduzeća koja imaju grupe. Želite li nastaviti?",
            "Stvarno želite pokrenuti masovno ažuriranje? Operacija može potrajati i utjecati na sve klijente.",
            "POSLJEDNJA PROVJERA: Sigurno pokrenuti ažuriranje za sva poduzeća?"
        ];

        for (const question of confirmations) {
            if (!window.confirm(question)) {
                return;
            }
        }

        setBulkUpdateError(null);
        setBulkUpdating(true);
        Axios.post(`/Company/UpdateAllAresesForAllCompanies`)
            .then(() => {
                alert("Pokrenuto ažuriranje Ares-a za sva poduzeća s grupama.");
            })
            .catch(reason => {
                console.log(reason);
                setBulkUpdateError(GetApiError(reason));
            })
            .finally(() => setBulkUpdating(false));
    }, []);

    const renderSteps = useCallback((values: AresDeploymentInstallationStepPoint[], steps: AresDeploymentGroupInstallationStep[]) => {
        const stepsOrdered = steps.filter(v => v.order !== undefined).sort(f => f.order!);
        const parsedValues = values.reduce<{ [key: string]: any }>((prev, curr) => {
            return ({...prev, [curr.aresDeploymentGroupInstallationStep?.variableName ?? ""]: curr.variableValue});
        }, {});
        return <Stack horizontal tokens={{childrenGap: 5}}>
            {stepsOrdered.map((f, i) => {
                var element;

                switch (f.variableType) {
                    case InstallationVariableType.String:
                        element = <span>{parsedValues[f.variableName]}</span>;
                        break;
                    case InstallationVariableType.Integer:
                        element = <span>{parsedValues[f.variableName]}</span>;
                        break;
                    case InstallationVariableType.Boolean:
                        element = <Checkbox checked={parsedValues[f.variableName]}/>;
                        break;
                    case InstallationVariableType.DateTime:
                        element = <span>{parsedValues[f.variableName]}</span>;
                        break;
                    case InstallationVariableType.Float:
                        element = <span>{parsedValues[f.variableName]}</span>;
                        break;
                }

                return <Stack horizontal tokens={{childrenGap: 5}}>
                    <span>{`${f.order}. ${f.variableName}`}</span>
                    <div>
                        {element}
                    </div>
                </Stack>;
            })}
        </Stack>
    }, []);

    const companiesColumns = useMemo(() => {
        return MakeTenantColumns<Company>({
            name: {
                minWidth: 300,
                render: item => <OpenCompany resource={item}/>
            },
            accountingCompanyId: {
                minWidth: 300,
                render: item => item.accountingCompany ? <OpenCompany resource={item.accountingCompany}/> : <span/>
            },
            aresGroups: {
                render: (item) => {
                    const companyIsLoading = isCompanyLoading(item.id);

                    return <Stack horizontal tokens={{childrenGap: 5}}>
                        <Stack tokens={{childrenGap: 10}}>
                            {item.aresDeploymentGroupCompanyMappings.map(f => {
                                return <Stack horizontal tokens={{childrenGap: 10}}>
                                    <span>
                                        {f.aresDeploymentGroup.name} [{AresDeploymentEnvironment[f.deploymentEnvironment ?? AresDeploymentEnvironment.Production]}]
                                    </span>
                                    {renderSteps(f.installationStepPoints, f.aresDeploymentGroup.installationSteps)}
                                </Stack>;
                            })}
                        </Stack>
                        {companyIsLoading ? <Spinner/> : <ActionButton style={{
                            maxHeight: "12px !important"
                        }} styles={{
                            root: {
                                maxHeight: "12px !important"
                            },
                            flexContainer: {
                                maxHeight: "12px !important"
                            }
                        }} disabled={companyIsLoading} iconProps={{iconName: "Upload"}}
                                                                       onClick={() => updateAllAres(item.id)}>
                            Ažuriraj sve arese
                        </ActionButton>}
                        {companyErrors[item.id] ?
                            <span style={{
                                color: "red",
                                wordWrap: "break-word",
                                whiteSpace: "break-spaces"
                            }}>{companyErrors[item.id]}</span> : null}
                    </Stack>;
                },
            },
        }, t);
    }, [t, isCompanyLoading, updateAllAres, companyIsLoading, selectedCompany, SetSelectedCompany, updatingCompanies]);

    return <TitleAndContentLayout title={"Ares deployments"} pad>
        {/*{JSON.stringify(updatingCompanies)}*/}
        {!!updatingCompanies ? Object.keys(updatingCompanies).length ? <Stack horizontal tokens={{childrenGap: 10}}>
            {Object.keys(updatingCompanies).map(key => <ProgressIndicator key={key} title={key} label={key}/>)}
        </Stack> : null : null}
        <Pivot>
            <PivotItem headerText={"Ares deployments"}>
                <p>Glavna implementacijska baza: {mainDeploymentCompany?.connectionName}</p>
                <p>Glavna testna baza: {mainTestDeploymentCompany?.connectionName ?? "Nije postavljena"}</p>
                <TenantList<AresDeploymentGroup, AresDeploymentGroup, AresDeploymentGroup>
                    additionalCommands={batchPrepareCommands}
                    additionalSelectionCommands={groupCommands}
                    title={"Ares deployment groups"}
                    resource={TenantResource.AresDeploymentGroup}
                    reloadRef={reloadGroupsRef}
                    baseDto={{}}
                    form={groupForm}
                    columns={groupColumns}
                    orderBy={"name"}
                    orderDescending={true}
                    selectionModeOverride={SelectionMode.multiple}
                    selectionChanged={item => setAresGroupSelection(item)}
                    selectionItemsChanged={setSelectedAresGroups}
                />
                <div>
                    {aresGroupSelection ? <Stack tokens={{childrenGap: 10}}>
                        <Stack tokens={{childrenGap: 8}} styles={{root: {padding: "8px 0"}}}>
                            <Stack horizontal horizontalAlign={"space-between"} verticalAlign={"center"}>
                                <Text variant={"mediumPlus"}>Snapshot versions</Text>
                                <Stack horizontal tokens={{childrenGap: 6}}>
                                    <ActionButton
                                        iconProps={{iconName: "BulletedList"}}
                                        disabled={!aresGroupSelection?.id}
                                        onClick={() =>
                                            navigate(AppRoute.ARES_DEPLOYMENT_GROUP_INSTALLED_COMPANIES(aresGroupSelection?.id))
                                        }>
                                        Instalirana poduzeća
                                    </ActionButton>
                                    <ActionButton
                                        iconProps={{iconName: "OpenInNewWindow"}}
                                        disabled={!aresGroupSelection?.id}
                                        onClick={() =>
                                            navigate(AppRoute.ARES_DEPLOYMENT_GROUP_SNAPSHOTS(aresGroupSelection?.id))
                                        }>
                                        Upravljaj svim verzijama
                                    </ActionButton>
                                    <AresDeploymentPrepareSnapshotCallout
                                        keyData={`selected-group-prepare-snapshot-${aresGroupSelection.id}`}
                                        groupId={aresGroupSelection.id}
                                        groupName={aresGroupSelection.name}
                                        suggestedVersionName={nextSnapshotVersionName}
                                        disabled={!aresGroupSelection?.id || groupSnapshotsLoading}
                                        onVersionsPulled={() => refreshAresesAfterVersionPull(aresGroupSelection.id)}
                                        onTestsFinished={refreshGroupsAfterTests}
                                        onSnapshotCreated={() => refreshAfterPreparedSnapshot(aresGroupSelection.id)}
                                    />
                                </Stack>
                            </Stack>

                            {groupSnapshotsLoading ? <Spinner label={"Učitavam snapshote..."}/> : null}

                            {!groupSnapshotsLoading && groupSnapshots.length === 0 ? (
                                <Text variant={"small"}>Nema snapshot verzija za ovu grupu.</Text>
                            ) : null}

                            {!groupSnapshotsLoading && groupSnapshots.length > 0 ? (
                                <Stack tokens={{childrenGap: 6}}>
                                    <Text variant={"small"}>
                                        Prikaz: {compactGroupSnapshots.length} od ukupno {groupSnapshots.length} snapshota
                                    </Text>
                                    {compactGroupSnapshots.map(snapshot => (
                                        <Stack
                                            key={snapshot.id}
                                            horizontal
                                            wrap
                                            verticalAlign={"center"}
                                            tokens={{childrenGap: 8}}
                                            styles={{root: {border: "1px solid #e1dfdd", padding: 8, borderRadius: 4}}}
                                        >
                                            <Text>
                                                <b>{snapshot.name || `v${snapshot.versionNo}`}</b>
                                                {` (v${snapshot.versionNo})`}
                                            </Text>
                                            <Text variant={"small"}>
                                                {snapshot.snapshotTakenAt
                                                    ? moment(snapshot.snapshotTakenAt).format("DD.MM.YYYY. HH:mm")
                                                    : "-"}
                                            </Text>
                                            <Text variant={"small"}>Hash: {snapshot.sourceHash || "-"}</Text>
                                            <ActionButton
                                                iconProps={{iconName: snapshot.isTesting ? "CheckMark" : "CircleRing"}}
                                                onClick={() =>
                                                    setSnapshotEnvironment(
                                                        snapshot.id,
                                                        AresDeploymentEnvironment.Testing,
                                                        !snapshot.isTesting,
                                                        aresGroupSelection.id
                                                    )
                                                }>
                                                Testing
                                            </ActionButton>
                                            <ActionButton
                                                iconProps={{iconName: snapshot.isStaging ? "CheckMark" : "CircleRing"}}
                                                onClick={() =>
                                                    setSnapshotEnvironment(
                                                        snapshot.id,
                                                        AresDeploymentEnvironment.Staging,
                                                        !snapshot.isStaging,
                                                        aresGroupSelection.id
                                                    )
                                                }>
                                                Staging
                                            </ActionButton>
                                            <ActionButton
                                                iconProps={{iconName: snapshot.isProduction ? "CheckMark" : "CircleRing"}}
                                                onClick={() =>
                                                    setSnapshotEnvironment(
                                                        snapshot.id,
                                                        AresDeploymentEnvironment.Production,
                                                        !snapshot.isProduction,
                                                        aresGroupSelection.id
                                                    )
                                                }>
                                                Production
                                            </ActionButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : null}
                        </Stack>

                        <AresDeploymentRolloutSection
                            groupId={aresGroupSelection.id}
                            onReleased={refreshAfterRolloutRelease}
                        />

                        <TenantList<AresDeploymentAres, AresDeploymentAres, AresDeploymentAres>
                            title={`Ares deployments for group: ${aresGroupSelection.name}`}
                            resource={TenantResource.AresDeploymentAres}
                            baseDto={aresBaseDto}
                            form={aresForm}
                            orderBy={"aresVersion"}
                            queryIsRequired={true}
                            query={{
                                aresDeploymentGroupId: {
                                    Equals: aresGroupSelection.id
                                }
                            }}
                            reloadRef={reloadAresesRef}
                            columns={aresColumns}
                        />

                        <TenantList<AresDeploymentWorkflowDefinition, AresDeploymentWorkflowDefinition, AresDeploymentWorkflowDefinition>
                            title={`Workflow definitions for group: ${aresGroupSelection.name}`}
                            resource={TenantResource.AresDeploymentWorkflowDefinition}
                            baseDto={{aresDeploymentGroupId: aresGroupSelection.id}}
                            form={workflowForm}
                            queryIsRequired={true}
                            query={{
                                aresDeploymentGroupId: {
                                    Equals: aresGroupSelection.id
                                }
                            }}
                            columns={workflowColumns}
                        />

                        <TenantList<AresDeploymentGroupInstallationStep, AresDeploymentGroupInstallationStep, AresDeploymentGroupInstallationStep>
                            title={`Installation steps for group: ${aresGroupSelection.name}`}
                            resource={TenantResource.AresDeploymentGroupInstallationStep}
                            baseDto={{aresDeploymentGroupId: aresGroupSelection.id}}
                            form={aresDeploymentGroupInstallationStepForm}
                            queryIsRequired={true}
                            query={{
                                aresDeploymentGroupId: {
                                    Equals: aresGroupSelection.id
                                }
                            }}
                            columns={aresDeploymentGroupInstallationStepColumns}
                        />
                    </Stack> : null}
                </div>
            </PivotItem>
            <PivotItem headerText={"Poduzeća s ares implementacijom"}>
                <Stack horizontal tokens={{childrenGap: 10}} verticalAlign={"center"} styles={{root: {marginBottom: 10}}}>
                    <ActionButton
                        iconProps={{iconName: "Play"}}
                        disabled={bulkUpdating}
                        onClick={updateAllCompaniesWithAres}>
                        {bulkUpdating ? "Pokrećem…" : "Pokreni ažuriranje za sva poduzeća"}
                    </ActionButton>
                    {bulkUpdateError && <Text variant={"small"} styles={{root: {color: "#a80000"}}}>{bulkUpdateError}</Text>}
                </Stack>
                <TenantList<Company, CompanyDto, Company>
                    pad
                    additionalSelectionCommands={item => {
                        return [
                            <ActionButton
                                key={`update-all-ares-${item.id}`}
                                disabled={!item || !item.aresDeploymentGroupCompanyMappings || item.aresDeploymentGroupCompanyMappings.length === 0 || isCompanyLoading(item.id)}
                                iconProps={{iconName: "Edit"}}
                                onClick={() => updateAllAres(item.id)}>Ažuriraj sve arese</ActionButton>
                        ];
                    }}
                    resource={TenantResource.Company}
                    addingTitle={"adding-new-company"}
                    allowSearching
                    allowedGroupKeys={{
                        accountingCompanyId: (val, dto) => dto.accountingCompany?.name ?? "- "
                    }}
                    quickFilters={{
                        accountingCompanyId: {
                            type: new CustomSearchBoxElement<Company, CompanyDto>({
                                url: "Company/List",
                                key: "id",
                                titleKey: "name",
                                customSearchBoxType: "DEFAULT_CRUD",
                                subtitleKey: "description",
                                resource: TenantResource.Company,
                            }),
                        }
                    }}
                    stretchForm
                    headers={{
                        "onlyWithAresDeployment": "true",
                        "showEvenNotOurCustomers": "true"
                    }}
                    baseDto={{}}
                    selectionChanged={SetSelectedCompany}
                    columns={companiesColumns}
                ></TenantList>
            </PivotItem>
        </Pivot>
    </TitleAndContentLayout>;
}
